<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import Router from 'svelte-spa-router';
  import { location } from 'svelte-spa-router';
  import { socketService } from './services/socketService';
  import { soundService } from './services/soundService';
  import { connectionStore, setConnectionStatus, ConnectionStatus } from './stores/connectionStore';
  import { lobbyStore, updateLobby } from './stores/lobbyStore';
  import { playerStore } from './stores/playerStore';
  import { gameStore } from './stores/gameStore';
  import { editorStore } from './stores/editorStore';
  import { announceToScreenReader, getConnectionStatusMessage } from './lib/accessibility';
  import LobbyEntry from './components/LobbyEntry.svelte';
  import LobbyWaitingRoom from './components/LobbyWaitingRoom.svelte';
  import PromptSelection from './components/game/PromptSelection.svelte';
  import ClueBuilder from './components/game/ClueBuilder.svelte';
  import PresenterIntro from './components/game/PresenterIntro.svelte';
  import PresentationView from './components/game/PresentationView.svelte';
  import RoundEnd from './components/game/RoundEnd.svelte';
  import GameEnd from './components/game/GameEnd.svelte';
  import AudioControls from './components/AudioControls.svelte';
  import Editor from './routes/Editor.svelte';
  import { clueBuilderStore } from './stores/clueBuilderStore';
  import type { Socket } from 'socket.io-client';
  import type { ServerToClientEvents, ClientToServerEvents } from '@blather/shared';
  import { GamePhase } from '@blather/shared';
  import { SoundCategory } from './types/audio';

  // Warn before leaving with unsaved changes (editor)
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', (e) => {
      if ($editorStore.hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    });
  }

  let socket: Socket<ServerToClientEvents, ClientToServerEvents> | null = null;
  let currentMusicId: string | null = null;

  // View routing based on lobby and game state
  $: gamePhase = $lobbyStore.lobby?.gameState?.phase;
  $: currentView = !$lobbyStore.lobby ? 'entry'
    : gamePhase === GamePhase.PROMPT_SELECTION ? 'prompt-selection'
    : gamePhase === GamePhase.OPENER_BUILDING ? 'opener-building'
    : gamePhase === GamePhase.PRESENTER_INTRO ? 'presenter-intro'
    : gamePhase === GamePhase.PRESENTING ? 'presenting'
    : gamePhase === GamePhase.ROUND_END ? 'round-end'
    : gamePhase === GamePhase.GAME_END ? 'game-end'
    : gamePhase ? 'game-in-progress'
    : 'waiting-room';

  $: connectionMessage = getConnectionStatusMessage($connectionStore.status);

  // Log view changes for debugging
  $: if (currentView) {
    console.log('Current view:', currentView, '(phase:', gamePhase, ')');
  }

  // Handle phase-specific background music
  async function handlePhaseMusic(phase: GamePhase) {
    // Stop current music with fade out
    if (currentMusicId) {
      await soundService.stop(currentMusicId, 1000);
      currentMusicId = null;
    }

    // Map phases to music files
    const musicMap: Record<string, string | null> = {
      [GamePhase.WAITING]: '/audio/music/waiting-room.ogg',
      [GamePhase.PROMPT_SELECTION]: '/audio/music/prompt-selection.ogg',
      [GamePhase.OPENER_BUILDING]: '/audio/music/opener-building.ogg',
      [GamePhase.PRESENTING]: '/audio/music/presenting.ogg',
      [GamePhase.PRESENTER_INTRO]: null, // No music during intro
      [GamePhase.ROUND_END]: null,
      [GamePhase.GAME_END]: null,
    };

    const musicFile = musicMap[phase];
    if (musicFile) {
      currentMusicId = await soundService.play(musicFile, {
        category: SoundCategory.MUSIC,
        loop: true,
        volume: 0.6,
        fadeIn: 1000,
      });
    }
  }

  // Resume audio on user interaction (browser requirement)
  function handleUserInteraction() {
    if (soundService.isSuspended()) {
      soundService.resume().catch((err) => {
        console.warn('Failed to resume audio:', err);
      });
    }
  }

  onMount(() => {
    const serverUrl = import.meta.env.VITE_SERVER_URL || '';
    const isProduction = !import.meta.env.VITE_SERVER_URL;
    console.log('Connecting to server:', serverUrl || '(same origin)', isProduction ? 'via /api' : 'direct');

    socket = socketService.connect(serverUrl, isProduction ? { path: '/api/socket.io' } : {});

    // Connection event handlers
    socket.on('connect', async () => {
      console.log('Connected to server');
      setConnectionStatus(ConnectionStatus.CONNECTED);
      announceToScreenReader('Connected to server');

      // Initialize audio system
      try {
        await soundService.initialize();
        await soundService.resume();
      } catch (error) {
        console.warn('Audio initialization delayed (user interaction required)');
      }

      // Preload critical sounds
      await soundService.preload([
        '/audio/ui/phase-change.ogg',
        '/audio/ui/success.ogg',
        '/audio/ui/error.ogg',
        '/audio/ui/notification.ogg',
        '/audio/sfx/button-click.ogg',
        '/audio/music/waiting-room.ogg',
      ]);
    });

    socket.on('disconnect', (reason) => {
      console.log('Disconnected from server:', reason);
      setConnectionStatus(ConnectionStatus.DISCONNECTED);
      announceToScreenReader('Disconnected from server', 'assertive');
    });

    socket.on('connect_error', (error) => {
      console.error('Connection error:', error);
      setConnectionStatus(ConnectionStatus.ERROR, error.message);
      announceToScreenReader('Connection error occurred', 'assertive');
    });

    socket.io.on('reconnect_attempt', () => {
      console.log('Attempting to reconnect...');
      setConnectionStatus(ConnectionStatus.RECONNECTING);
    });

    socket.io.on('reconnect', () => {
      console.log('Reconnected to server');
      setConnectionStatus(ConnectionStatus.CONNECTED);
      announceToScreenReader('Reconnected to server');
    });

    // Lobby event handlers
    socket.on('lobby:updated', (lobby) => {
      console.log('Lobby updated:', lobby);
      updateLobby(lobby);
    });

    socket.on('lobby:playerJoined', (player) => {
      console.log('Player joined:', player);
      announceToScreenReader(`${player.username} joined the lobby`);
    });

    socket.on('lobby:playerLeft', (playerId) => {
      console.log('Player left:', playerId);
      announceToScreenReader('A player left the lobby');
    });

    socket.on('lobby:playerReconnected', (player) => {
      console.log('Player reconnected:', player);
      announceToScreenReader(`${player.username} reconnected`);
    });

    socket.on('lobby:statusChanged', (status) => {
      console.log('Lobby status changed:', status);
      announceToScreenReader(`Lobby status changed to ${status.replace('_', ' ')}`);
    });

    socket.on('error', (error) => {
      console.error('Server error:', error);
      announceToScreenReader(`Error: ${error.message}`, 'assertive');
    });

    // Game event handlers
    socket.on('game:stateUpdate', (gameState) => {
      console.log('Game state updated:', gameState);
      gameStore.setGameState(gameState);
      // Update lobby with new game state
      if ($lobbyStore.lobby) {
        updateLobby({ ...$lobbyStore.lobby, gameState });
      }
    });

    socket.on('game:phaseChange', (phase, metadata) => {
      console.log('Game phase changed:', phase, metadata);
      announceToScreenReader(`Game phase: ${phase.replace('_', ' ')}`, 'assertive');

      // Play phase transition sound
      soundService.play('/audio/ui/phase-change.ogg', {
        category: SoundCategory.UI,
        volume: 0.8,
      });

      // Handle phase music
      handlePhaseMusic(phase);
    });

    socket.on('game:promptsAvailable', (prompts) => {
      console.log('Prompts available:', prompts);
      gameStore.setAvailablePrompts(prompts);
    });

    socket.on('game:openerFrameworksAvailable', (data) => {
      console.log('Opener frameworks available:', data);
      clueBuilderStore.setFrameworks(data);
      announceToScreenReader('Opening clue builder', 'polite');
    });

    socket.on('game:openerSubmitted', () => {
      console.log('Opener submitted successfully');
      announceToScreenReader('Opener submitted successfully. Waiting for other players.', 'assertive');

      // Play success sound
      soundService.play('/audio/ui/success.ogg', {
        category: SoundCategory.UI,
      });
    });

    socket.on('game:error', (error) => {
      console.error('Game error:', error);
      gameStore.setError(error.message);
      announceToScreenReader(`Game error: ${error.message}`, 'assertive');

      // Play error sound
      soundService.play('/audio/ui/error.ogg', {
        category: SoundCategory.UI,
        volume: 0.9,
      });
    });

    // Set initial status as connecting
    setConnectionStatus(ConnectionStatus.CONNECTING);

    // Add user interaction listener for audio resume
    document.addEventListener('click', handleUserInteraction, { once: true });
  });

  onDestroy(() => {
    if (socket) {
      socketService.disconnect();
    }
    // Clean up user interaction listener
    document.removeEventListener('click', handleUserInteraction);
  });
</script>

<main>
  {#if $location.startsWith('/edit')}
    <!-- Editor Route -->
    <Editor />
  {:else}
    <!-- Game Routes -->
    {#if currentView === 'entry'}
      <LobbyEntry />
    {:else if currentView === 'waiting-room'}
      <LobbyWaitingRoom />
    {:else if currentView === 'prompt-selection'}
      <PromptSelection />
    {:else if currentView === 'opener-building'}
      <ClueBuilder />
    {:else if currentView === 'presenter-intro'}
      <PresenterIntro />
    {:else if currentView === 'presenting'}
      <PresentationView />
    {:else if currentView === 'round-end'}
      <RoundEnd />
    {:else if currentView === 'game-end'}
      <GameEnd />
    {:else if currentView === 'game-in-progress'}
      <div class="game-placeholder">
        <p>Game in progress (phase: {gamePhase})</p>
        <p>Additional game phases will be implemented in future phases.</p>
      </div>
    {/if}

    {#if $connectionStore.status === ConnectionStatus.DISCONNECTED || $connectionStore.status === ConnectionStatus.RECONNECTING}
      <div class="connection-status" role="status" aria-live="polite">
        {connectionMessage}
      </div>
    {/if}

    {#if $connectionStore.status === ConnectionStatus.ERROR && $connectionStore.error}
      <div class="connection-error" role="alert">
        Connection Error: {$connectionStore.error}
      </div>
    {/if}

    <!-- Audio Controls -->
    <AudioControls />
  {/if}
</main>

<style>
  main {
    min-height: 100vh;
    padding: 0;
  }

  .connection-status {
    position: fixed;
    bottom: 1rem;
    right: 1rem;
    background-color: var(--color-warning);
    color: white;
    padding: 1rem 1.5rem;
    border-radius: var(--border-radius);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    font-weight: 600;
    z-index: 1000;
  }

  .connection-error {
    position: fixed;
    bottom: 1rem;
    right: 1rem;
    background-color: var(--color-error);
    color: white;
    padding: 1rem 1.5rem;
    border-radius: var(--border-radius);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    font-weight: 600;
    z-index: 1000;
    max-width: 300px;
  }

  @media (max-width: 600px) {
    .connection-status,
    .connection-error {
      bottom: 0.5rem;
      right: 0.5rem;
      left: 0.5rem;
      max-width: none;
    }
  }
</style>
